import React from 'react';
import { ProgressStepper, Step, StepButton, StepLabel } from '@one-thd/sui-atomic-components';
import { AuthorizeCard, Calendar, ShipToHome, Van } from '@one-thd/sui-icons';
import { FULFILLMENT_TYPES } from './constants';
import { getActiveSteps, getDeliveryTrackerActiveStep, getProgressStepTitles } from './utils';

// Ported one-to-one from @thd-olt-component-react/active-orders'
// components/fulfillment-details/FulfillmentDeliveryTracker.tsx (OOP-4059), with ONE deliberate
// substitution: the original renders `TrackingStepper` from `@delivery/tracking` for the
// ship-to-home/BOSS/BODFS/appliance branch. That package ships a build defect (relative ESM
// imports with no file extensions — invalid under Node's strict ESM resolver, breaks this
// package's own production prerender/build, not just local dev) that isn't safe to carry as a
// dependency until it's fixed upstream (tracked separately; not owned by the carousel team
// either). Per investigation, TrackingStepper itself is a thin wrapper: it maps a
// status/deliveryType/timeliness string triad onto the SAME ProgressStepper/Step/StepButton/
// StepLabel primitives already used below for the BOPIS branch, plus a delay/exception alert
// tooltip. This local DELIVERY_STEP_TITLES + getDeliveryTrackerActiveStep replicates the step
// visualization; the delay/exception alert embellishment is intentionally omitted as a low-risk
// simplification (see PR discussion) — revisit once the real package is importable.
const DELIVERY_ICONS = [AuthorizeCard, Van, Van, ShipToHome];
const DELIVERY_STEP_TITLES = ['Processed', 'Shipped', 'Out for Delivery', 'Delivered'];
const APPLIANCE_ICONS = [AuthorizeCard, Calendar, Van, ShipToHome];

export interface FulfillmentDeliveryTrackerProps {
  fulfillmentGroup?: any;
  isMobile?: boolean;
}

function getTrackingStatus(deliveryTrackerData: any, statusDescription?: string): string {
  const trackerStatus = deliveryTrackerData?.status;
  if (trackerStatus === 'ORDER_RECEIVED' && deliveryTrackerData?.deliveryWindow?.date) {
    return 'SCHEDULED';
  }
  return trackerStatus ?? statusDescription?.replace(/ /g, '_')?.toUpperCase() ?? '';
}

export const FulfillmentDeliveryTracker: React.FC<FulfillmentDeliveryTrackerProps> = ({ fulfillmentGroup, isMobile }) => {
  const statusCode = fulfillmentGroup?.lineItems?.[0]?.statusCode;

  if (
    fulfillmentGroup?.fulfillmentType === FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE
    || fulfillmentGroup?.fulfillmentType === FULFILLMENT_TYPES.WILL_CALL_FULFILLMENT
  ) {
    const steps = getProgressStepTitles(fulfillmentGroup?.fulfillmentType);
    const icons = [AuthorizeCard, ShipToHome, ShipToHome];
    return (
      <ProgressStepper
        leadingLabel=""
        activeStep={getActiveSteps(statusCode ?? 0)}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        data-testid="fulfillment-stepper"
      >
        {steps.map((step, index) => (
          <Step key={step.title} aria-label={step.title.concat('.')}>
            <StepButton icon={icons[index] ?? AuthorizeCard} />
            <StepLabel title={step.title} subtitle={step.subtitle} />
          </Step>
        ))}
      </ProgressStepper>
    );
  }

  if (
    fulfillmentGroup?.fulfillmentType === FULFILLMENT_TYPES.BUY_ONLINE_SHIP_TO_STORE
    || fulfillmentGroup?.fulfillmentType === FULFILLMENT_TYPES.SHIP_TO_HOME
    || fulfillmentGroup?.fulfillmentType === FULFILLMENT_TYPES.BUY_ONLINE_DELIVER_FROM_STORE
    || fulfillmentGroup?.fulfillmentType === FULFILLMENT_TYPES.APPLIANCE
  ) {
    const statusDescription = fulfillmentGroup?.lineItems?.[0]?.statusDescription;
    const status = getTrackingStatus(fulfillmentGroup?.deliveryTrackerData, statusDescription);
    const isAppliance = fulfillmentGroup?.fulfillmentType === FULFILLMENT_TYPES.APPLIANCE;
    const icons = isAppliance ? APPLIANCE_ICONS : DELIVERY_ICONS;

    return (
      <ProgressStepper
        leadingLabel=""
        activeStep={getDeliveryTrackerActiveStep(status)}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        data-testid="fulfillment-delivery-tracking-stepper"
      >
        {DELIVERY_STEP_TITLES.map((title, index) => (
          <Step key={title} aria-label={title.concat('.')}>
            <StepButton icon={icons[index] ?? AuthorizeCard} />
            <StepLabel title={title} />
          </Step>
        ))}
      </ProgressStepper>
    );
  }

  return null;
};
